import { markPageJsExecuted } from "@faire/web--source/common/performance/TrackPerformanceBreakdown/markPageJsExecuted";
import { UPLTHookOptions } from "@faire/web--source/common/performance/userPerceivedLoadTiming/useUserPerceivedLoadTiming";
import { ViewLayoutPlacement } from "@faire/web-api--source/indigofair/data/ViewLayoutPlacement";
import { useEffect, useState } from "react";

import { useIsBrowseFpsLoadedAndHasNoResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/filterablePaginatedSection/useIsBrowseFPSLoadedAndHasNoResults";
import { useBrowseLayoutDefinedFilteredComponentsQuery } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useBrowseLayoutDefinedFilteredComponentsQuery/useBrowseLayoutDefinedFilteredComponentsQuery";
import { useCategoryPageQueryKeys } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/useCategoryPageQueryKeys";
import { useBrowseV3LayoutQuery } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/v3/useBrowseV3LayoutQuery";
import { useIsBrowseV3FpsLayoutLoadedAndHasNoResults } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/DataFetching/v3/useIsBrowseV3FpsLayoutLoadedAndHasNoResults";
import { useCategoryPageActionName } from "@faire/retailer/views/category/__internal__/CategoryPageRoot/__internal__/LoggedInCategoryPage/__internal__/Base/__internal__/useCategoryPageActionName";

const useIgnoreImages = () => {
  const isBrowseV3FpsLayoutLoadedAndHasNoResults =
    useIsBrowseV3FpsLayoutLoadedAndHasNoResults();
  const isBrowseV3FpsLayoutError = useBrowseV3LayoutQuery(
    ViewLayoutPlacement.FPS
  ).isError;

  const { isError: isFpsError } =
    useBrowseLayoutDefinedFilteredComponentsQuery();
  const isFpsLoadedAndHasNoResults = useIsBrowseFpsLoadedAndHasNoResults();

  return (
    isBrowseV3FpsLayoutLoadedAndHasNoResults ||
    isBrowseV3FpsLayoutError ||
    isFpsError ||
    isFpsLoadedAndHasNoResults
  );
};

const usePageLoadKey = (): string => {
  const queryKey = useCategoryPageQueryKeys();

  return JSON.stringify(queryKey);
};

const pageJsExecutedMark = markPageJsExecuted();

export const useSDUICategoryPageUPLT = (): Omit<
  UPLTHookOptions,
  "numComponents"
> => {
  const [lastPageLoadKey, setLastPageLoadKey] = useState<string | undefined>(
    undefined
  );

  const pageName = useCategoryPageActionName();

  const ignoreImages = useIgnoreImages();
  const pageLoadKey = usePageLoadKey();

  const {
    isError: isBrowseV3FpsLayoutError,
    isFetching: isBrowseV3FpsLayoutFetching,
    data: browseV3FpsLayoutData,
    dataUpdatedAt: browseV3FpsLayoutUpdatedAt,
  } = useBrowseV3LayoutQuery(ViewLayoutPlacement.FPS);
  const isBrowseV3FpsLayoutLoading =
    isBrowseV3FpsLayoutFetching || browseV3FpsLayoutData === undefined;

  const {
    data: filteredComponentsData,
    isError: isFilteredComponentsError,
    isFetching: isFilteredComponentsFetching,
    dataUpdatedAt: filteredComponentsDataUpdatedAt,
  } = useBrowseLayoutDefinedFilteredComponentsQuery();

  /**
   * The effect below is necessary to handle the case where PAGE_UPDATE
   * is triggered for a page with cached data. In this case no data is actually
   * fetched but we want to momentarily override the page loading state to true
   * so that UPLT measurement doesn't complete prematurely before the new components
   * are mounted.
   *
   * Without this, the UPLT measurement reports a status of TARGET_COMPONENT_UNMOUNTED
   * for the previous components and doesn't wait for the cached components to mount.
   */
  useEffect(() => {
    if (lastPageLoadKey !== pageLoadKey) {
      setLastPageLoadKey(pageLoadKey);
    }
  }, [pageLoadKey, lastPageLoadKey]);

  const isFilteredComponentsLoading =
    isFilteredComponentsFetching || filteredComponentsData === undefined;

  const isLayoutLoading =
    isBrowseV3FpsLayoutLoading && !isBrowseV3FpsLayoutError;
  const isPageLoading =
    lastPageLoadKey !== pageLoadKey ||
    isLayoutLoading ||
    (isFilteredComponentsLoading && !isFilteredComponentsError);

  const dataUpdatedAt = Math.max(
    filteredComponentsDataUpdatedAt,
    browseV3FpsLayoutUpdatedAt
  );

  return {
    name: pageName,
    isPageLoading,
    ignoreImages,
    enableTrackPerformanceBreakdown: "internal-tracking",
    pageLoadKey,
    dataUpdatedAt,
    pageJsExecutedMark: pageJsExecutedMark,
  };
};
